import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel, Delete, Add, ContentCopy } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

function PartnerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [partner, setPartner] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [stats, setStats] = useState({ clicks: 0, conversions: 0, revenue: 0, daily: [] });
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState(new Date());
  const [openCampaignDialog, setOpenCampaignDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [customPayout, setCustomPayout] = useState('');
  const [customPayoutType, setCustomPayoutType] = useState('fixed');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', country: '', status: '',
    postback_url: '', internal_notes: ''
  });

  useEffect(() => {
    fetchPartner();
    fetchCampaigns();
  }, [id]);

  useEffect(() => {
    if (tabValue === 2) {
      fetchStats();
    }
  }, [tabValue, startDate, endDate]);

  const fetchPartner = async () => {
    try {
      const response = await axios.get(`/admin/partners/${id}`);
      const data = response.data.data;
      setPartner(data);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        country: data.country || '',
        status: data.status || '',
        postback_url: data.postback_url || '',
        internal_notes: data.internal_notes || ''
      });
    } catch (error) {
      console.error('Error fetching partner:', error);
      setMessage({ type: 'error', text: 'Failed to load partner' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const [assignedRes, availableRes] = await Promise.all([
        axios.get(`/admin/partners/${id}/campaigns`),
        axios.get(`/admin/partners/${id}/available-campaigns`)
      ]);
      setCampaigns(assignedRes.data.data || []);
      setAvailableCampaigns(availableRes.data.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`/admin/partners/${id}/stats`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/admin/partners/${id}`, formData);
      setPartner({ ...partner, ...formData });
      setEditMode(false);
      setMessage({ type: 'success', text: 'Partner saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save partner' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData({
      name: partner.name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      country: partner.country || '',
      status: partner.status || '',
      postback_url: partner.postback_url || '',
      internal_notes: partner.internal_notes || ''
    });
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    try {
      await axios.put(`/admin/partners/${id}/password`, { password: newPassword });
      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setOpenPasswordDialog(false);
      setNewPassword('');
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update password' });
    }
  };

  const handleAssignCampaign = async () => {
    if (!selectedCampaign) return;
    try {
      await axios.post(`/admin/partners/${id}/campaigns`, {
        campaign_id: selectedCampaign,
        custom_payout: customPayout || null,
        custom_payout_type: customPayoutType
      });
      await fetchCampaigns();
      setOpenCampaignDialog(false);
      setSelectedCampaign('');
      setCustomPayout('');
      setCustomPayoutType('fixed');
      setMessage({ type: 'success', text: 'Campaign assigned successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to assign campaign' });
    }
  };

  const handleRemoveCampaign = async (campaignId) => {
    if (!window.confirm('Remove this campaign from partner?')) return;
    try {
      await axios.delete(`/admin/partners/${id}/campaigns/${campaignId}`);
      await fetchCampaigns();
      setMessage({ type: 'success', text: 'Campaign removed successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove campaign' });
    }
  };

  const copyTrackingLink = (campaignId) => {
    const link = `https://trcktoo.com/track?campaign_id=${campaignId}&partner_id=${id}`;
    navigator.clipboard.writeText(link);
    setMessage({ type: 'success', text: 'Tracking link copied!' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!partner) {
    return (
      <Box>
        <Typography color="error">Partner not found</Typography>
        <Button onClick={() => navigate('/partners')}>Back to Partners</Button>
      </Box>
    );
  }

  const tabs = ['General', 'Campaigns', 'Stats'];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/partners')}>Back</Button>
          <Typography variant="h5" fontWeight={700} flexGrow={1}>{partner.name}</Typography>
          <Chip 
            label={partner.status === 'active' ? 'Active' : 'Inactive'} 
            color={partner.status === 'active' ? 'success' : 'error'} 
            sx={{ color: 'white' }}
          />
        </Box>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((label, i) => <Tab key={i} label={label} />)}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Card>
          <CardContent>
            {/* General Tab */}
            {tabValue === 0 && (
              <Box>
                {/* Header with Edit button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Partner Information</Typography>
                  {!editMode ? (
                    <Button startIcon={<Edit />} variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button startIcon={<Save />} variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button startIcon={<Cancel />} variant="outlined" onClick={handleCancelEdit}>Cancel</Button>
                    </Box>
                  )}
                </Box>

                {/* Section: Basic Info */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={600} mb={2}>Basic Information</Typography>
                  <Box display="flex" gap={4} flexWrap="wrap">
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Name</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.name || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Email</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.email || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Phone</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.phone || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Country</Typography>
                      {editMode ? (
                        <TextField fullWidth size="small" value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} sx={{ mt: 0.5 }} />
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.country || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 15%', minWidth: 100 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Status</Typography>
                      {editMode ? (
                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                          <Select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="deleted">Deleted</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.status || '-'}</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: '1 1 20%', minWidth: 150 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Created At</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                        {partner.created_at ? new Date(partner.created_at).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                 {/* Section: Account Information */}
<Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
  <Typography variant="subtitle2" color="primary" fontWeight={600} mb={2}>Account Information</Typography>
  <Box display="flex" gap={4} flexWrap="wrap">
    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
      <Typography variant="caption" color="textSecondary" fontWeight={600}>Partner ID</Typography>
      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.partner_id || 'N/A'}</Typography>
    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Login Email</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{partner.user?.email || partner.email}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>Last Login</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                        {partner.last_login ? new Date(partner.last_login).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <Button variant="outlined" size="small" onClick={() => setOpenPasswordDialog(true)}>
                      Change Password
                    </Button>
                  </Box>
                </Paper>

                {/* Section: Postback Configuration */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={600} mb={2}>Postback Configuration</Typography>
                  <Box>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>Postback URL</Typography>
                    {editMode ? (
                      <TextField 
                        fullWidth 
                        size="small" 
                        value={formData.postback_url} 
                        onChange={e => setFormData({...formData, postback_url: e.target.value})} 
                        sx={{ mt: 0.5 }} 
                        placeholder="https://your-system.com/postback?lead_id={lead_id}&status={status}"
                        helperText="Placeholders: {click_id}, {lead_id}, {payout}, {status}, {sub1}-{sub5}, {campaign_id}, {country}, {timestamp}"
                      />
                    ) : (
                      <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                        {partner.postback_url || '-'}
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Section: Notes */}
                <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#fffde7', borderColor: '#ffe082' }}>
                  <Typography variant="subtitle2" color="warning.dark" fontWeight={600} mb={2}>Notes</Typography>
                  {editMode ? (
                    <TextField fullWidth multiline rows={4} value={formData.internal_notes} onChange={e => setFormData({...formData, internal_notes: e.target.value})} placeholder="Internal notes about this partner..." />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: partner.internal_notes ? 'inherit' : 'text.secondary' }}>
                      {partner.internal_notes || 'No notes'}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {/* Campaigns Tab */}
            {tabValue === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Assigned Campaigns</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenCampaignDialog(true)}
                    disabled={availableCampaigns.length === 0}
                  >
                    Assign Campaign
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Default Payout</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Custom Payout</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tracking Link</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.length > 0 ? campaigns.map((campaign) => (
                        <TableRow 
                          key={campaign.campaign_id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography 
                              color="primary.main" 
                              fontWeight={500}
                              onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell>€{parseFloat(campaign.payout || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            {campaign.custom_payout 
                              ? <Chip label={`€${parseFloat(campaign.custom_payout).toFixed(2)} (${campaign.custom_payout_type})`} size="small" color="primary" />
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<ContentCopy />}
                              onClick={() => copyTrackingLink(campaign.campaign_id)}
                            >
                              Copy Link
                            </Button>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCampaign(campaign.campaign_id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="textSecondary">No campaigns assigned</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Stats Tab */}
            {tabValue === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Statistics</Typography>
                  <Box display="flex" gap={2}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </Box>
                </Box>
                
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Clicks</Typography>
                      <Typography variant="h4" fontWeight={600}>{stats.clicks}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Conversions</Typography>
                      <Typography variant="h4" fontWeight={600}>{stats.conversions}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>Revenue</Typography>
                      <Typography variant="h4" fontWeight={600} color="success.main">€{stats.revenue.toFixed(2)}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {stats.daily && stats.daily.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Conversions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.daily.map((day) => (
                          <TableRow key={day.date}>
                            <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                            <TableCell align="right">{day.conversions}</TableCell>
                            <TableCell align="right">€{parseFloat(day.revenue).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Assign Campaign Dialog */}
        <Dialog open={openCampaignDialog} onClose={() => setOpenCampaignDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Campaign</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={selectedCampaign}
                label="Campaign"
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                {availableCampaigns.map((c) => (
                  <MenuItem key={c.campaign_id} value={c.campaign_id}>
                    {c.name} (Default: €{parseFloat(c.payout || 0).toFixed(2)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Custom Payout (optional)"
              type="number"
              value={customPayout}
              onChange={(e) => setCustomPayout(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Leave empty to use campaign default"
            />
            <FormControl fullWidth>
              <InputLabel>Payout Type</InputLabel>
              <Select
                value={customPayoutType}
                label="Payout Type"
                onChange={(e) => setCustomPayoutType(e.target.value)}
              >
                <MenuItem value="fixed">Fixed (CPA)</MenuItem>
                <MenuItem value="percentage">Percentage (RevShare)</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCampaignDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAssignCampaign} disabled={!selectedCampaign}>
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mt: 2 }}
              helperText="Minimum 6 characters"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleChangePassword} disabled={newPassword.length < 6}>
              Update Password
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default PartnerDetails;
